import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
  _id: string;
  email: string;
  username?: string;
  phone?: string;
  role: 'super_admin' | 'company_admin' | 'store_manager' | 'customer';
  status: 'active' | 'inactive' | 'suspended';
  profile?: {
    lastName?: string;
    firstName?: string;
    lastNameKana?: string;
    firstNameKana?: string;
    birthDate?: string;
    gender?: 'male' | 'female' | 'other';
    address?: string;
  };
  license: {
    number: string;
    type?: string[];
    issueDate?: string;
    expiryDate?: string;
    issuingAuthority?: string;
  };
  statistics?: {
    totalOrders: number;
    totalSpent: number;
    membershipLevel: 'bronze' | 'silver' | 'gold' | 'platinum';
    joinDate: string;
    lastOrderDate?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// 从localStorage恢复状态
const getInitialState = (): AuthState => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    
    if (token && userString) {
      const user = JSON.parse(userString);
      return {
        user,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    }
  } catch (error) {
    // 如果解析失败，清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  
  return {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
      // 保存用户信息到localStorage
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      // 清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const { loginStart, loginSuccess, loginFailure, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;